import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { eventQueries } from "../features/events/api/queries";
import { EventRow } from "../features/events/components/EventRow";
import type { EventListItem } from "../features/events/types";
import { useOffset } from "../shared/lib/paging";
import { AppHeader } from "../shared/ui/AppHeader";
import { Pager } from "../shared/ui/Pager";

/**
 * How many marked events a page holds.
 *
 * This list only grows — it is the record of everything the viewer has ever
 * marked, past events included — so it needs a bound even though most people's
 * is short.
 */
const PAGE_SIZE = 20;

/**
 * Both statuses in **one** request, grouped client-side.
 *
 * Two requests would need two paginations that could disagree about where the
 * page boundary falls, and would double the forecast batching work for no gain.
 * `includeCancelled` is the deliberate exception to "every read filters
 * deleted_at IS NULL": an event you marked that was later called off should stay
 * here, struck through, rather than vanishing without explanation. This is what
 * the soft-delete decision in Phase 2 was for.
 */
/*
 * A function, not a shared constant: the query key is derived from this object,
 * so a mutable module-level literal risks being the same reference in two places
 * that then disagree. Building it fresh keeps the key stable by value.
 */
const myEventsQuery = (offset: number) =>
  eventQueries.page({
    limit: PAGE_SIZE,
    offset,
    filter: {
      attendance: ["ATTENDING", "INTERESTED"],
      includeCancelled: true,
      /*
       * Explicitly opted out of the upcoming-only default, for the same reason as
       * `includeCancelled` above: this page is the record of what you marked, and
       * something you went to last month is part of that record. The flat listing
       * hides the past because it is a discovery view; this is not one.
       */
      upcomingOnly: false,
    },
  });

export const Route = createFileRoute("/_authenticated/events/mine")({
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(myEventsQuery(0)),
  component: MyEventsPage,
});

function MyEventsPage() {
  const page = useOffset(PAGE_SIZE);
  const { data } = useSuspenseQuery(myEventsQuery(page.offset));

  const events = data.eventPage.items;
  const total = data.eventPage.totalCount;
  page.reconcile(total);

  /*
   * Grouped by status rather than left in one date-ordered list: "am I going to
   * this?" is the question this page answers, and interleaving the two answers
   * makes it harder to read than separating them. Date order is preserved within
   * each group, since the server sorted it.
   *
   * ⚠️ **The grouping is within the page, not across the list.** The server
   * orders by start time and knows nothing about these two buckets, so a page
   * boundary can fall inside either one — page 2 can open with more "Going" rows
   * after page 1 already showed a "Going" section.
   *
   * Accepted rather than fixed. The alternatives are worse: two paginated
   * queries would need two independent pagers on one screen, and asking the
   * server to sort by status would fight the date order that makes the page
   * readable. The group headings count what is on this page, and the pager below
   * says which page that is.
   */
  const attending = events.filter((e) => e.myAttendance === "ATTENDING");
  const interested = events.filter((e) => e.myAttendance === "INTERESTED");

  return (
    <>
      <AppHeader />
      <main className="p-6">
        <h1 className="text-2xl font-semibold">My events</h1>
        <p className="mt-1 text-sm text-content-muted">
          Everything you’ve marked. Cancelled events stay here, struck through.
        </p>

        {events.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-border-subtle p-10 text-center">
            <p className="font-medium">Nothing marked yet</p>
            <p className="mt-1 text-sm text-content-muted">
              Mark an event interested or going and it will show up here.
            </p>
            <Link
              to="/events"
              className="mt-4 inline-block rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover"
            >
              Browse events
            </Link>
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-8">
            <Group title="Going" events={attending} />
            <Group title="Interested" events={interested} />
          </div>
        )}

        <Pager
          offset={page.offset}
          limit={PAGE_SIZE}
          totalCount={total}
          onOffsetChange={page.setOffset}
          noun="marked events"
        />
      </main>
    </>
  );
}

/**
 * One status group, or nothing at all when empty.
 *
 * An empty heading with no rows under it reads as a loading failure, so the
 * heading only appears when it has something to head.
 */
function Group({ title, events }: { title: string; events: EventListItem[] }) {
  if (events.length === 0) return null;

  return (
    <section>
      <h2 className="text-sm font-medium tracking-wide text-content-muted uppercase">
        {title} ({events.length})
      </h2>
      <ul className="mt-3 flex flex-col gap-3">
        {events.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </ul>
    </section>
  );
}
